import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { TOTAL_CHANNELS } from "@/components/daw/dawHelpers";
import { useEngineAnimationLoop } from "@/components/daw/useEngineAnimationLoop";

// iter-197 — Live waveform history capacity (bars). Older samples slide out.
const LIVE_WAVE_CAP = 96;

/**
 * Track-list live meters — extracted verbatim from ELPMStudioDAW.
 * Owns the rAF-driven per-channel peaks, master peak, playhead, recording
 * meters + clip detection (iter-200/202/219 flash / badge / history) and the
 * 20 Hz live-wave rev counter consumed by WaveLane / MixerRow.
 */
export default function useLiveMeters({ engine, channels, recordingIdx, setPlaying, setStreamBytesUploaded }) {
  const [peaks, setPeaks] = useState(() => new Array(TOTAL_CHANNELS).fill(0));
  const [masterPeak, setMasterPeak] = useState(0);
  const [playhead, setPlayhead] = useState(0);
  // Live per-channel recording state — used by WaveLane/MixerRow to render the
  // growing red region + scrolling peak meter. Map of channel index → {peak, sec}.
  const [recordingMeters, setRecordingMeters] = useState({});
  const recordingMetersRef = useRef(recordingMeters);
  useEffect(() => { recordingMetersRef.current = recordingMeters; }, [recordingMeters]);
  // Clip detector — { [ch]: {hotFrames, lastWarnedAt} } persists across rAF frames.
  const clipStateRef = useRef({});
  const livePlayPeaksRef = useRef({}); // {ch: Float32Array(96)} — circular
  const livePlayPeaksHeadRef = useRef({}); // {ch: nextWriteIdx}
  const liveRecPeaksRef = useRef({}); // {ch: Float32Array(96)}
  const liveRecPeaksHeadRef = useRef({});
  const [liveWaveRev, setLiveWaveRev] = useState(0);
  const liveWaveLastBumpRef = useRef(0);
  const clipStreakRef = useRef({}); // {ch: consecutive-hot-sample count}
  const clipFlashRef = useRef({}); // {ch: expiresAt ms} — 200 ms bar flash
  const clipBadgeRef = useRef({}); // {ch: expiresAt ms} — 1500 ms header badge
  const recentClipsRef = useRef([]);
  const [clipCount, setClipCount] = useState(0);
  const clipCountRef = useRef(0);
  const clipHistoryRef = useRef([]);
  const clipHistoryRevRef = useRef(0);
  const [clipHistoryOpen, setClipHistoryOpen] = useState(false);
  const [clipHistoryRev, setClipHistoryRev] = useState(0);

  const isClipFlashing = (ch) => {
    const until = clipFlashRef.current[ch];
    if (!until) return false;
    const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (now >= until) { delete clipFlashRef.current[ch]; return false; }
    return true;
  };
  const isClipBadgeActive = (ch) => {
    const until = clipBadgeRef.current[ch];
    if (!until) return false;
    const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (now >= until) { delete clipBadgeRef.current[ch]; return false; }
    return true;
  };
  const pushLiveSample = (kind, ch, peak) => {
    const cap = LIVE_WAVE_CAP;
    const bufMap = kind === "play" ? livePlayPeaksRef.current : liveRecPeaksRef.current;
    const headMap = kind === "play" ? livePlayPeaksHeadRef.current : liveRecPeaksHeadRef.current;
    let buf = bufMap[ch];
    if (!buf) {
      buf = new Float32Array(cap);
      bufMap[ch] = buf;
      headMap[ch] = 0;
    }
    const h = headMap[ch] | 0;
    buf[h] = peak;
    headMap[ch] = (h + 1) % cap;
    // iter-200 — clip-streak detector. ≥ 0.95 for 2 consecutive samples → flash.
    if (peak >= 0.95) {
      const c = (clipStreakRef.current[ch] || 0) + 1;
      clipStreakRef.current[ch] = c;
      if (c >= 2) {
        const nowMs = (typeof performance !== "undefined" ? performance.now() : Date.now());
        clipFlashRef.current[ch] = nowMs + 200; // 200 ms bar flash
        clipBadgeRef.current[ch] = nowMs + 1500; // 1.5 s lingering badge
        recentClipsRef.current.push({ ch, when: nowMs });
        const head = (engine?.getPlayhead?.() ?? 0);
        clipHistoryRef.current.push({ ch, whenMs: nowMs, at: head });
        if (clipHistoryRef.current.length > 50) clipHistoryRef.current.shift();
        clipHistoryRevRef.current++;
      }
    } else {
      clipStreakRef.current[ch] = 0;
    }
  };
  // Read out the buffer as a linear array (oldest → newest).
  const readLiveSamples = (kind, ch) => {
    const bufMap = kind === "play" ? livePlayPeaksRef.current : liveRecPeaksRef.current;
    const headMap = kind === "play" ? livePlayPeaksHeadRef.current : liveRecPeaksHeadRef.current;
    const buf = bufMap[ch];
    if (!buf) return null;
    const h = headMap[ch] | 0;
    const out = new Array(LIVE_WAVE_CAP);
    for (let i = 0; i < LIVE_WAVE_CAP; i++) out[i] = buf[(h + i) % LIVE_WAVE_CAP];
    return out;
  };
  const clearLiveSamples = (kind, ch) => {
    const bufMap = kind === "play" ? livePlayPeaksRef.current : liveRecPeaksRef.current;
    const headMap = kind === "play" ? livePlayPeaksHeadRef.current : liveRecPeaksHeadRef.current;
    delete bufMap[ch];
    delete headMap[ch];
  };

  // Per-channel volume state mirrors engine.channels[i].volume so that updates
  // from MIDI / preset-load / Voice TTS propagate to the UI through React state.
  const [channelVolumes, setChannelVolumes] = useState(() => channels.map((c) => c.volume));

  // rAF loop — onTick captured via ref so deps don't have to track engine internals
  useEngineAnimationLoop(() => {
    setPeaks(channels.map((c) => (c.buffer ? c.getPeak() : 0)));
    setMasterPeak(engine.getMasterPeak());
    // Automation replay — applies any newly-passed knob points to FX nodes.
    engine.tickAutomation();
    // Sidechain ducking — reads trigger peaks + drives target gains via setTargetAtTime.
    engine.tickSidechain();
    // Tempo-synced plugin host — broadcast transport tick to loaded plugins.
    engine._broadcastTransportTick();
    // Live bytes counter when streaming to disk — updates every ~16 ms.
    if (engine.isTracking()) {
      setStreamBytesUploaded(engine.getStreamBytesTotal());
    }
    // Collect per-channel recording peaks for every channel currently being
    // recorded into — drives the live red overlay in WaveLane/MixerRow.
    const isAnyTracking = engine.isTracking?.() || recordingIdx !== null;
    if (isAnyTracking) {
      const next = {};
      const HOT_TH = 0.97;       // peak threshold considered "clipping"
      const HOT_FRAMES_NEEDED = 6; // ~100 ms at 60 fps before warning
      const COOL_FRAMES = 30;    // ~500 ms below threshold clears the flag
      const TOAST_COOLDOWN_MS = 3000;
      const now = Date.now();
      const clipState = clipStateRef.current;
      const computeMeter = (ch) => {
        const peak = engine.getRecordingPeak(ch);
        const sec = engine.getRecordingElapsed(ch);
        const cs = clipState[ch] || (clipState[ch] = { hot: 0, cool: 0, lastWarned: 0, on: false });
        if (peak >= HOT_TH) {
          cs.hot++; cs.cool = 0;
          if (cs.hot >= HOT_FRAMES_NEEDED && !cs.on) {
            cs.on = true;
            if (now - cs.lastWarned > TOAST_COOLDOWN_MS) {
              cs.lastWarned = now;
              const name = engine.channels[ch]?.name || `Channel ${String(ch + 1).padStart(2, "0")}`;
              toast.warning(`${name} is clipping`, { description: "Lower the input gain on your audio interface or mic preamp." });
            }
          }
        } else {
          cs.cool++;
          if (cs.cool >= COOL_FRAMES) { cs.hot = 0; cs.on = false; }
        }
        return { peak, sec, clipping: cs.on };
      };
      if (recordingIdx !== null) next[recordingIdx] = computeMeter(recordingIdx);
      const tk = engine.trackingSession;
      if (tk?.recorders) for (const r of tk.recorders) next[r.ch] = computeMeter(r.ch);
      setRecordingMeters(next);
      for (const k in next) pushLiveSample("rec", Number(k), next[k].peak);
    } else if (Object.keys(recordingMetersRef.current).length > 0) {
      // Stopped — wipe so the red overlay disappears.
      clipStateRef.current = {};
      setRecordingMeters({});
      const had = liveRecPeaksRef.current;
      for (const k in had) clearLiveSamples("rec", Number(k));
    }
    // iter-197 — sample per-channel PLAYBACK peaks while transport is rolling.
    if (engine.playing) {
      for (let i = 0; i < channels.length; i++) {
        const c = channels[i];
        if (!c?.buffer) continue;
        const p = c.getPeak?.();
        if (typeof p === "number") pushLiveSample("play", i, p);
      }
    } else if (Object.keys(livePlayPeaksRef.current).length > 0) {
      const had = livePlayPeaksRef.current;
      for (const k in had) clearLiveSamples("play", Number(k));
    }
    // Bump the rev counter at ~20 Hz so React picks up the fresh buffers
    // without churning every 16 ms.
    const nowMs = (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (nowMs - liveWaveLastBumpRef.current >= 50) {
      liveWaveLastBumpRef.current = nowMs;
      setLiveWaveRev((r) => (r + 1) | 0);
      // iter-202 — prune clip events older than 4 s and surface the count
      // to React only when it actually changes.
      const cutoff = nowMs - 4000;
      const list = recentClipsRef.current;
      let dropFront = 0;
      while (dropFront < list.length && list[dropFront].when < cutoff) dropFront++;
      if (dropFront > 0) list.splice(0, dropFront);
      const len = list.length;
      if (len !== clipCountRef.current) {
        clipCountRef.current = len;
        setClipCount(len);
      }
      // iter-219 — when the Clip History dropdown is open, push the rev
      // tracking ref into React state so newly-clipped channels appear live.
      if (clipHistoryOpen && clipHistoryRevRef.current !== clipHistoryRev) {
        setClipHistoryRev(clipHistoryRevRef.current);
      }
      // When transport stops, ALSO wipe the clip count so a stale "3 clips"
      // pill doesn't linger after the user hit Stop.
      if (!engine.playing && !recordingMetersRef.current && Object.keys(recordingMetersRef.current || {}).length === 0) {
        if (len > 0 && nowMs - (list[list.length - 1]?.when || 0) > 1500) {
          list.length = 0;
          clipCountRef.current = 0;
          setClipCount(0);
        }
      }
    }
    // Detect & propagate volume changes — diff against last frame so we don't
    // spam React with identical arrays every 16 ms.
    setChannelVolumes((prev) => {
      let changed = false;
      const next = prev.slice();
      for (let i = 0; i < channels.length; i++) {
        const v = Math.round(channels[i].volume);
        if (v !== prev[i]) {
          next[i] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    const head = engine.getPlayhead();
    setPlayhead(head);
    // Loop wrap takes priority over auto-stop
    if (engine.playing && engine.loopEnabled && engine.loopEnd > engine.loopStart && head >= engine.loopEnd) {
      engine.seekTo(engine.loopStart);
      return;
    }
    if (engine.playing && engine.maxDuration > 0 && head >= engine.maxDuration) {
      engine.stop();
      setPlaying(false);
    }
  });

  return {
    peaks,
    masterPeak,
    playhead,
    setPlayhead,
    recordingMeters,
    liveWaveRev,
    clipCount,
    clipHistoryRef,
    clipHistoryRevRef,
    clipHistoryOpen,
    setClipHistoryOpen,
    clipHistoryRev,
    setClipHistoryRev,
    isClipFlashing,
    isClipBadgeActive,
    readLiveSamples,
    channelVolumes,
  };
}
